import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getAccountSettings } from '../../services/settings';
import { isSystemAdmin, fetchAccountFlags } from '../../services/auth';
import { getFolderStatus } from '../../services/folder';
import UserMenu from './UserMenu';
import './Header.css';

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [logoUrl, setLogoUrl] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [mcpEnabled, setMcpEnabled] = useState(false);
  const [ordersEnabled, setOrdersEnabled] = useState(false);
  const settingsRef = useRef(null);

  useEffect(() => {
    loadSettings();
    checkFolderStatus();
  }, [location.pathname]);

  // LAKPED-8: la pestaña Pedidos solo existe para cuentas con orders_enabled.
  // El flag no cambia al navegar, así que se pide una sola vez.
  useEffect(() => {
    fetchAccountFlags()
      .then(account => setOrdersEnabled(Boolean(account.orders_enabled)))
      .catch(() => setOrdersEnabled(false));
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (settingsRef.current && !settingsRef.current.contains(event.target)) {
        setSettingsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadSettings = async () => {
    try {
      const settings = await getAccountSettings();
      if (settings.logo_url) {
        setLogoUrl(`${process.env.REACT_APP_API_URL}${settings.logo_url}`);
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    }
  };

  const checkFolderStatus = async () => {
    try {
      const status = await getFolderStatus();
      setMcpEnabled(status.enabled);
    } catch (error) {
      // MCP not configured for this account
      setMcpEnabled(false);
    }
  };

  const handleAccountSettings = () => {
    setSettingsOpen(false);
    navigate('/account-settings');
  };

  const handlePrompts = () => {
    setSettingsOpen(false);
    navigate('/prompts');
  };

  const isSysAdmin = isSystemAdmin();

  return (
    <header className="header">
      <div className="header-left">
        {logoUrl && (
          <img src={logoUrl} alt="Logo" className="company-logo" />
        )}
      </div>

      <nav className="header-nav">
        <button
          className={`nav-tab ${location.pathname === '/similarity-search' ? 'active' : ''}`}
          onClick={() => navigate('/similarity-search')}
        >
          {ordersEnabled ? 'Entrada' : 'Análisis'}
        </button>
        {ordersEnabled && (
          <button
            className={`nav-tab ${location.pathname.startsWith('/offers') ? 'active' : ''}`}
            onClick={() => navigate('/offers')}
          >
            Ofertas
          </button>
        )}
        {ordersEnabled && (
          <button
            className={`nav-tab ${location.pathname.startsWith('/orders') ? 'active' : ''}`}
            onClick={() => navigate('/orders')}
          >
            Pedidos
          </button>
        )}
        {ordersEnabled && (
          <button
            className={`nav-tab ${location.pathname === '/panel' ? 'active' : ''}`}
            onClick={() => navigate('/panel')}
          >
            Panel
          </button>
        )}
        <button
          className={`nav-tab ${location.pathname === '/history' ? 'active' : ''}`}
          onClick={() => navigate('/history')}
        >
          Historial
        </button>
        {mcpEnabled && (
          <button
            className={`nav-tab ${location.pathname === '/folder-browser' ? 'active' : ''}`}
            onClick={() => navigate('/folder-browser')}
          >
            Carpeta
          </button>
        )}
      </nav>

      <div className="header-actions">
        <div className="settings-menu-container" ref={settingsRef}>
          <button className="btn-settings" onClick={() => setSettingsOpen(!settingsOpen)}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>
            </svg>
          </button>

          {settingsOpen && (
            <div className="settings-dropdown">
              <button onClick={handleAccountSettings} className="settings-item">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="3"/>
                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
                </svg>
                Configuración de cuenta
              </button>

              {isSysAdmin && (
                <button onClick={handlePrompts} className="settings-item">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                    <polyline points="14 2 14 8 20 8"/>
                    <line x1="16" y1="13" x2="8" y2="13"/>
                    <line x1="16" y1="17" x2="8" y2="17"/>
                    <polyline points="10 9 9 9 8 9"/>
                  </svg>
                  Prompts
                </button>
              )}
            </div>
          )}
        </div>
        <UserMenu />
      </div>
    </header>
  );
};

export default Header;
